/**
 * API is responsible to all logic related to endpoints.
 * It is responsible to get errors returned from endpoints in different formats and throw a single error message.
 * TODO: throw an object with message and optionally a code or other relevant information.
 */
import axios from "axios";
import {
  getHttpProtocol as scheme,
  getBillingServiceUrl as billingServiceUrl,
} from "../utils/requests";
import { refreshAccessTokenInterceptor } from "../utils/refreshTokens";
import {
  requestAccessTokenInterceptor,
  responseStatusInterceptor,
} from "../utils/axios.utils";
import type {
  PaymentMigrationResponseType,
  StripeBillingDataType,
} from "../pages/MigrationAssistant/types";

// custom axios

const billingAxios = axios.create({
  baseURL: `${scheme}//${billingServiceUrl}`,
  headers: { "Content-Type": "application/json" },
  validateStatus: null,
});

billingAxios.interceptors.request.use(requestAccessTokenInterceptor);

billingAxios.interceptors.request.use(refreshAccessTokenInterceptor);

billingAxios.interceptors.response.use(responseStatusInterceptor);

export const BILLING_TYPE = {
  CREDIT_CARD: "C",
  BANK_ACCOUNT: "A",
  AU_BECS_DEBIT: "D",
  BACS_DEBIT: "U",
  SEPA_DEBIT: "P",
  ACSS_DEBIT: "N",
  STRIPE_LINK: "L",
  COMP: "F",
  CASH: "S",
  PENDING: "Z",
} as const;

export type BillingType = (typeof BILLING_TYPE)[keyof typeof BILLING_TYPE];

export interface Billing {
  id: number;
  uuid: string;
  billingType: BillingType;
  customerId: number;
  isVerified: boolean;
  isPrimary: number;
  clientUuid: string;
  userUuid: string;
  billingBrand: string;
  billingLastFour: string;
  billingExpMonth: string;
  billingExpYear: string;
  isActive: number;
  isDeleted: number;
}

export interface GetUserBillingsResult {
  page: number;
  limit: number;
  hasMore: boolean;
  total: number;
  data: Billing[];
}

export type Invoice = {
  invoiceUuid: string;
  billingId: number;
  openTimestamp: number;
  nextAttemptTimestamp: number;
  total: number;
  isPending: boolean;
  billingType: BillingType;
  billingLastFour: string;
  billingBrand: string;
  isVerified: boolean;
};

export type PaymentHistoryItem = {
  invoiceId: number;
  invoiceUuid: string;
  orderId: number;
  transactionLogUuid: string;
  receiptId: number;
  status: "success" | "failure" | "pending";
  timestamp: number;
  amount: string;
  description: string;
  comped: boolean;
};

export async function getUserPaymentMethods(userUuid: string) {
  // &active=all returns all non-deleted payment methods, not just active
  const url = `/billing?user_uuid=${userUuid}&active=all`;
  const response = await billingAxios.get<GetUserBillingsResult>(url);

  if (response.data?.data && response.data.data.length > 0) {
    return response.data.data;
  }
  return null;
}

export async function getUserOutstandingInvoices(userUuid: string) {
  const url = `/invoice/${userUuid}/outstanding`;
  const response = await billingAxios.get<{ data: Invoice[] }>(url);
  return response.data.data;
}

export async function getUserPaymentHistory(userUuid: string) {
  const url = `/transactions/${userUuid}`;
  const response = await billingAxios.get<{ data: PaymentHistoryItem[] }>(url);
  return response.data.data;
}

export interface PaymentMethod {
  id: number;
  uuid: string;
  apiCustomerReference: string;
  apiPaymentReference: string;
  apiAuthNumber: string;
  billingType: string;
  gatewayId: number;
  customerId: number;
  isVerified: boolean;
  onPlatformAccount: boolean;
  clientUuid: string;
  userUuid: string;
  billingBrand: string;
  billingLastFour: string;
  billingExpMonth: number;
  billingExpYear: number;
  isPrimary: number;
  isActive: number;
  isDeleted: number;
}
export interface PostSavePaymentMethodResult {
  wasSuccessful: boolean;
  billing: PaymentMethod;
  updatedOpenInvoices: boolean;
  updatedSubscriptions: boolean;
}

export interface SavePaymentMethodBody {
  userUuid: string;
  clientUuid: string;
  paymentMethodId: string;
  customerId: string;
}

export interface CustomerStripeSubscriptionSummary {
  priceId: string;
  subscriptionId: string;
  status: string;
}

export const postSavePaymentMethod = async ({
  userUuid,
  clientUuid,
  paymentMethodId,
  customerId,
}: SavePaymentMethodBody) => {
  const response = await billingAxios.post<PostSavePaymentMethodResult>(
    "/billing",
    {
      userUuid,
      clientUuid,
      paymentReference: paymentMethodId,
      customerReference: customerId,
    },
  );
  return response.data;
};

export type CustomerDetails = {
  stripeAccountType: "platform" | "connect";
  stripePublishableKey?: string;
  clientSecret: string;
  stripeCustomer: string;
  acceptedPaymentMethodTypes: string[];
};
export interface StripeOptions {
  clientSecret: string;
  stripeCustomer: string;
  customerDetails: CustomerDetails[];
}

export const getStripeClientSecret = async (
  clientUuid: string,
  userUuid: string,
) => {
  const response = await billingAxios.post<StripeOptions>("/billing/secret", {
    clientUuid,
    userUuid,
  });
  return response.data;
};

export interface InvoicePreview {
  subtotal: number;
  subtotalRecurring?: number;
  total: number;
  totalRecurring: number;
  totalLessTxFeesAndTaxes: number;
  totalRecurringLessTxFeesAndTaxes?: number;
  discount?: {
    rate: number;
    amount: number;
    type: string;
  };
  discountRecurring?: {
    rate: number;
    amount: number;
    type: string;
  };
  currency: string;
  tax: {
    amount: number;
    rate: number;
  };
  taxRecurring?: {
    amount: number;
    rate: number;
  };
  fees: {
    rate: number;
    fee: number;
    description: string;
    code: string;
  }[];
  feesRecurring?: {
    rate: number;
    fee: number;
    description: string;
    code: string;
  }[];
}

export const getInvoicePlanPreview = async ({
  clientUuid,
  planUuid,
  planPriceUuid,
  billingUuid,
  discountUuid,
  ignoreSetupFee,
  customSetupFee,
  startDate,
}: {
  clientUuid: string;
  planUuid: string;
  planPriceUuid?: string;
  billingUuid: string;
  discountUuid: string;
  ignoreSetupFee: boolean;
  customSetupFee: number;
  startDate: string | undefined;
}) => {
  const response = await billingAxios.post<{ data: InvoicePreview }>(
    "/invoice/preview",
    {
      clientUuid,
      billingUuid,
      products: [
        {
          type: "PLAN",
          productUuid: planUuid,
          planPriceUuid,
          discountUuid,
          ignoreSetupFee,
          customSetupFee,
          startDate,
        },
      ],
    },
  );
  return response.data?.data;
};

export interface Discount {
  uuid: string;
  name: string;
  amount: number;
  discountType: {
    discountTypeId: number;
    discountTypeUuid: string;
    discountType: string;
    discountTypeCode: string;
  };
  code: string;
  slug: string;
  description: string;
  overCost: boolean;
  duration: number;
}

export interface CreateCheckoutSessionResponse {
  clientSecret: string;
}

export const getPlanDiscounts = async (clientUuid: string) => {
  const response = await billingAxios.get<{ data: Discount[] }>(
    `/${clientUuid}/discount?active=1&deleted=0&is_plan_discount=1`,
  );
  return response.data?.data;
};

export async function postStripeCustomerData(payload: {
  customerIds: string[];
}) {
  const response = await billingAxios.post<PaymentMigrationResponseType>(
    "/stripe/customers",
    {
      customerIds: payload.customerIds,
    },
  );
  return response.data;
}

export async function getStripeBillingData() {
  const response =
    await billingAxios.get<StripeBillingDataType>("/stripe/get-data");
  return response.data;
}

export interface CreateBillingPortalResponse {
  success: boolean;
  url?: string;
  message?: string;
}

export async function createBillingPortalSession(returnUrl: string) {
  const response = await billingAxios.post<{
    data: CreateBillingPortalResponse;
  }>("/stripe/create-billing-portal", { returnUrl });
  return response.data.data;
}

export async function getClientSubscriptionSummary(
  clientUuid: string,
): Promise<CustomerStripeSubscriptionSummary[]> {
  const response = await billingAxios.get<{
    data: CustomerStripeSubscriptionSummary[];
  }>(`/stripe/subscriptions/prices/${clientUuid}`);
  return response.data.data;
}

export async function createStripeCheckoutSession(params: {
  priceId: string;
  customerId: string;
  returnUrl: string;
}) {
  const response = await billingAxios.post<CreateCheckoutSessionResponse>(
    "/stripe/create-checkout-session",
    params,
  );
  return response.data;
}
